'use client';

import Image from 'next/image';

interface AuthLayoutProps {
  children: React.ReactNode;
}

export default function AuthLayout({ children }: AuthLayoutProps) {
  return (
    <div className="flex min-h-screen font-satoshi">
      {/* Left Section - Brand Design */}
      <div className="w-0 lg:w-[55%] relative overflow-hidden hidden lg:flex flex-col bg-gradient-to-br from-[#4F8EF7] via-[#5A7DE8] to-[#7B8FEB]">
        {/* Subtle Pattern Overlay */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
          backgroundSize: '32px 32px'
        }} />

        {/* Gradient Accents */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-violet-400/20 rounded-full blur-3xl" />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col h-full p-12 xl:p-16">
          {/* Top Section */}
          <div className="mb-auto">
            <span className="text-white text-sm font-medium">Powered by AI</span>
          </div>

          {/* Main Content */}
          <div className="flex-grow flex items-center">
            <div className="max-w-lg">
              <h1 className="text-4xl xl:text-5xl font-bold text-white mb-6 leading-[1.15]">
                Tax compliance,{' '}
                <span className="text-white">
                  simplified
                </span>
              </h1>
              <p className="text-lg text-white mb-10 leading-relaxed">
                Track notices, manage clients, and stay ahead of deadlines with AI-powered tools.
              </p>

              {/* Feature Cards */}
              <div className="space-y-3">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-white/15 border border-white/25 backdrop-blur-sm">
                  <div className="w-10 h-10 rounded-lg bg-white/25 flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">Centralized Dashboard</p>
                    <p className="text-white/90 text-xs">All clients, notices & demands in one view</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 rounded-xl bg-white/15 border border-white/25 backdrop-blur-sm">
                  <div className="w-10 h-10 rounded-lg bg-white/25 flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">AI-Powered Responses</p>
                    <p className="text-white/90 text-xs">Generate notice replies in seconds</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 rounded-xl bg-white/15 border border-white/25 backdrop-blur-sm">
                  <div className="w-10 h-10 rounded-lg bg-white/25 flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">Smart Reminders</p>
                    <p className="text-white/90 text-xs">Never miss a deadline again</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-auto pt-8 flex items-center justify-between">
            <div className="text-xs text-white/80">
              &copy; {new Date().getFullYear()} Finkr
            </div>
            <div className="flex items-center gap-4">
              <a href="#" className="text-xs text-white/80 hover:text-white transition-colors">Privacy</a>
              <a href="#" className="text-xs text-white/80 hover:text-white transition-colors">Terms</a>
            </div>
          </div>
        </div>

      </div>

      {/* Right Section - Form Area */}
      <div className="w-full lg:w-[45%] bg-white flex flex-col min-h-screen">
        {/* Logo - Fixed at top */}
        <div className="p-6 lg:p-8">
          <Image
            src="/finkr-logo.svg"
            alt="Finkr Logo"
            width={140}
            height={42}
            priority
            className="h-10 w-auto"
          />
        </div>

        {/* Form Container - Centered */}
        <div className="flex-1 flex items-center justify-center px-6 lg:px-12 pb-12">
          <div className="w-full max-w-sm">
            {children}
          </div>
        </div>

        {/* Footer - Mobile */}
        <div className="lg:hidden pb-6 text-center text-xs text-gray-500">
          &copy; {new Date().getFullYear()} Finkr. All rights reserved.
        </div>
      </div>
    </div>
  );
}
